import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import '../styles/admin.css';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'activo', 'inactivo'

    // Modales
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        nombre: '',
        apellido: '',
        domicilio: '',
        telefono: '',
        password: ''
    });

    // Obtener pacientes (solo rol 'paciente')
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/usuarios?search=${search}&page=${page}&limit=10`);
            // Filtrar solo pacientes
            let pacientes = res.data.users.filter(u => u.role === 'paciente');
            // Aplicar filtro de estado
            if (filterStatus === 'activo') {
                pacientes = pacientes.filter(u => u.active === true);
            } else if (filterStatus === 'inactivo') {
                pacientes = pacientes.filter(u => u.active === false);
            }
            setUsers(pacientes);
            setTotalPages(res.data.pages);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar pacientes');
        } finally {
            setLoading(false);
        }
    }, [search, page, filterStatus]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // Acciones rápidas
    const toggleActive = async (id) => {
        await api.patch(`/admin/usuarios/${id}/status`);
        fetchUsers();
    };
    const toggleApproval = async (id) => {
        await api.patch(`/admin/usuarios/${id}/approval`);
        fetchUsers();
    };
    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este paciente?')) return;
        await api.delete(`/admin/usuarios/${id}`);
        fetchUsers();
    };

    // Abrir edición
    const openEdit = (user) => {
        setCurrentUser(user);
        setFormData({
            email: user.email,
            nombre: user.nombre || '',
            apellido: user.apellido || '',
            domicilio: user.domicilio || '',
            telefono: user.telefono || '',
            password: ''
        });
        setShowEditModal(true);
    };

    // Guardar edición
    const saveEdit = async (e) => {
        e.preventDefault();
        await api.put(`/admin/usuarios/${currentUser._id}`, formData);
        setShowEditModal(false);
        fetchUsers();
    };

    // Guardar creación
    const saveCreate = async (e) => {
        e.preventDefault();
        await api.post('/admin/usuarios', { ...formData, role: 'paciente' });
        setShowCreateModal(false);
        fetchUsers();
        setFormData({ email: '', nombre: '', apellido: '', domicilio: '', telefono: '', password: '' });
    };

    // Helpers para renderizar insignias
    const getStatusBadge = (user) => {
        const active = user.active;
        const verified = user.emailVerified;
        return (
            <span className={`estado-badge ${active ? 'active' : 'inactive'}`}>
                <span className={`estado-dot ${active ? 'active' : 'inactive'}`}></span>
                {active ? 'Activo' : 'Inactivo'}
                {!verified && ' (pendiente)'}
            </span>
        );
    };

    const getInitials = (nombre, apellido) => {
        if (!nombre && !apellido) return '?';
        return `${nombre?.charAt(0) || ''}${apellido?.charAt(0) || ''}`.toUpperCase();
    };

    // Estadísticas
    const total = users.length;
    const activos = users.filter(u => u.active).length;
    const inactivos = total - activos;

    // Renderizado de vista grid (tarjetas)
    const renderGrid = () => (
        <div className="clients-grid">
            {users.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">👤</div>
                    <div className="empty-state-title">No hay pacientes</div>
                    <div className="empty-state-text">Agrega un nuevo paciente usando el botón "+ Nuevo paciente"</div>
                </div>
            ) : (
                users.map(user => (
                    <div key={user._id} className="client-card">
                        <div className="client-card-header">
                            <div className="client-avatar">
                                {getInitials(user.nombre, user.apellido)}
                            </div>
                            <div className="client-header-info">
                                <div className="client-name">{user.nombre} {user.apellido}</div>
                                <div className="client-email">{user.email}</div>
                            </div>
                            <div>{getStatusBadge(user)}</div>
                        </div>
                        <div className="client-card-body">
                            <div className="client-info-row">📱 {user.telefono || 'Sin teléfono'}</div>
                            <div className="client-info-row">🏠 {user.domicilio || 'Sin domicilio'}</div>
                            <div className="client-info-row">
                                {user.emailVerified ? '✅ Verificado' : '⏳ Pendiente de verificación'}
                            </div>
                        </div>
                        <div className="client-card-footer">
                            <div className="btn-group-actions">
                                <button className="btn-client-action btn-edit" onClick={() => openEdit(user)}>
                                    ✏️ Editar
                                </button>
                                <button
                                    className={`btn-client-action ${user.active ? 'btn-inactive' : 'btn-active'}`}
                                    onClick={() => toggleActive(user._id)}
                                >
                                    {user.active ? '🔒 Desactivar' : '✅ Activar'}
                                </button>
                                <button
                                    className={`btn-client-action ${user.emailVerified ? 'btn-warning' : 'btn-success'}`}
                                    onClick={() => toggleApproval(user._id)}
                                >
                                    {user.emailVerified ? '⛔ Rechazar' : '✅ Verificar'}
                                </button>
                                <button className="btn-client-action btn-delete" onClick={() => handleDelete(user._id)}>
                                    🗑️ Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    // Renderizado de vista lista
    const renderList = () => (
        <div className="clients-list">
            {users.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">👤</div>
                    <div className="empty-state-title">No hay pacientes</div>
                    <div className="empty-state-text">Agrega un nuevo paciente usando el botón "+ Nuevo paciente"</div>
                </div>
            ) : (
                users.map(user => (
                    <div key={user._id} className="client-list-item">
                        <div className="client-list-info">
                            <div className="client-list-header">
                                <div className="client-list-avatar">
                                    {getInitials(user.nombre, user.apellido)}
                                </div>
                                <div className="client-list-details">
                                    <div className="client-list-name">{user.nombre} {user.apellido}</div>
                                    <div className="client-list-contact">
                                        <span>📧 {user.email}</span>
                                        <span>📱 {user.telefono || 'Sin teléfono'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="client-list-status">{getStatusBadge(user)}</div>
                        <div className="client-list-actions">
                            <button className="btn-client-action btn-edit" onClick={() => openEdit(user)}>✏️</button>
                            <button
                                className={`btn-client-action ${user.active ? 'btn-inactive' : 'btn-active'}`}
                                onClick={() => toggleActive(user._id)}
                            >
                                {user.active ? '🔒' : '✅'}
                            </button>
                            <button
                                className={`btn-client-action ${user.emailVerified ? 'btn-warning' : 'btn-success'}`}
                                onClick={() => toggleApproval(user._id)}
                            >
                                {user.emailVerified ? '⛔' : '✅'}
                            </button>
                            <button className="btn-client-action btn-delete" onClick={() => handleDelete(user._id)}>🗑️</button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <div className="user-management">
            {/* Header *
            <div className="admin-page-header" style={{ marginBottom: '1.5rem' }}>
                <h1>👥 Gestión de Pacientes</h1>
                <p className="admin-page-subtitle">Administrá los pacientes de la plataforma</p>
            </div>*/}

            {/* ESTADÍSTICAS COMPACTAS */}
            <div className="stats-compact">
                <div className="stat-card-compact primary">
                    <div className="stat-icon">👥</div>
                    <div className="stat-content">
                        <div className="stat-value">{total}</div>
                        <div className="stat-label">Total Pacientes</div>
                    </div>
                </div>
                <div className="stat-card-compact success">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <div className="stat-value">{activos}</div>
                        <div className="stat-label">Activos</div>
                    </div>
                </div>
                <div className="stat-card-compact danger">
                    <div className="stat-icon">🚫</div>
                    <div className="stat-content">
                        <div className="stat-value">{inactivos}</div>
                        <div className="stat-label">Inactivos</div>
                    </div>
                </div>
                <div className="stat-card-compact info" style={{ cursor: 'pointer' }} onClick={() => setShowCreateModal(true)}>
                    <div className="stat-icon">➕</div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ fontSize: '1.1rem', marginTop: '0.2rem' }}>Nuevo</div>
                    </div>
                </div>
            </div>

            {/* Barra de herramientas */}
            <div className="clients-section">
                <div className="section-header">
                    <div className="section-title">
                        👥 Lista de Pacientes
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                            ({users.length} mostrados)
                        </span>
                    </div>
                    <div className="section-actions">
                        <div className="search-box">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Buscar por nombre, email o teléfono..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="filter-group">
                            <button
                                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('all')}
                            >
                                Todos
                            </button>
                            <button
                                className={`filter-btn ${filterStatus === 'activo' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('activo')}
                            >
                                ✅ Activos
                            </button>
                            <button
                                className={`filter-btn ${filterStatus === 'inactivo' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('inactivo')}
                            >
                                🚫 Inactivos
                            </button>
                        </div>
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                            >
                                ▦ Grid
                            </button>
                            <button
                                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                ≡ Lista
                            </button>
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {loading ? (
                    <div className="spinner-container"><div className="spinner"></div></div>
                ) : (
                    viewMode === 'grid' ? renderGrid() : renderList()
                )}

                {/* Paginación */}
                <nav>
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                        </li>
                        <li className="page-item"><span className="page-link">{page} de {totalPages}</span></li>
                        <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                        </li>
                    </ul>
                </nav>
            </div>

            {/* Modal Editar */}
            {showEditModal && (
                <div className="modal-backdrop" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">Editar paciente</h5>
                            <button className="btn-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <form onSubmit={saveEdit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input type="email" className="form-control" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nombre</label>
                                    <input type="text" className="form-control" value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Apellido</label>
                                    <input type="text" className="form-control" value={formData.apellido} onChange={e => setFormData({ ...formData, apellido: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Domicilio</label>
                                    <input type="text" className="form-control" value={formData.domicilio} onChange={e => setFormData({ ...formData, domicilio: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Teléfono</label>
                                    <input type="text" className="form-control" value={formData.telefono} onChange={e => setFormData({ ...formData, telefono: e.target.value })} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                                <button type="submit" className="btn btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Crear */}
            {showCreateModal && (
                <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">Nuevo paciente</h5>
                            <button className="btn-close" onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <form onSubmit={saveCreate}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input type="email" className="form-control" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Contraseña *</label>
                                    <input type="password" className="form-control" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nombre</label>
                                    <input type="text" className="form-control" value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Apellido</label>
                                    <input type="text" className="form-control" value={formData.apellido} onChange={e => setFormData({ ...formData, apellido: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Domicilio</label>
                                    <input type="text" className="form-control" value={formData.domicilio} onChange={e => setFormData({ ...formData, domicilio: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Teléfono</label>
                                    <input type="text" className="form-control" value={formData.telefono} onChange={e => setFormData({ ...formData, telefono: e.target.value })} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancelar</button>
                                <button type="submit" className="btn btn-success">Crear paciente</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;